// @ts-nocheck
import React, { useCallback, useRef, useEffect, useState } from 'react';
import {
  Typography,
  Grid,
  Box,
  Tabs,
  Tab,
  Button,
  IconButton,
} from '@mui/material';
import Editor from '@monaco-editor/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { withTransaction } from '@elastic/apm-rum-react';
import PropTypes from 'prop-types';
import SwalToast from '../../components/Common/SwalToast';
import useCustomTheme from '../../hooks/useCustomTheme';
import REGEX from '../../const/Regex';
import AddIconDialog from '../../components/AddIconDialog';
import utils from '../../utils/Utils';

const PRODUCT_ICON_LOGO = 'Product Icon';
const BROWSER_ICON_LOGO = 'Browser Icon';
const CLIENT_ICON_LOGO = 'Client Icon';
const LIGHT_THEME_NAME = 'light';
const DARK_THEME_NAME = 'dark';

/**
 * @function ColorPalette
 * @description Component for managing color palette preferences.
 * @returns {JSX.Element} The ColorPalette component.
 */
const CustomThemeConfig = () => {
  const { state: locationState } = useLocation();
  const { orgName, productDetails } = locationState || {};
  const navigate = useNavigate();

  const clientName = productDetails?.name.toLowerCase();
  const { fetchThemeConfiguration, saveTheme, resetToDefaultTheme } =
    useCustomTheme();

  const { user } = useSelector((reduxState) => reduxState);
  const userTheme = useSelector((reduxState) => reduxState.common.userTheme);
  const activatedOrgName = useSelector((state) => state.common.orgName);

  const [tabIndex, setTabIndex] = useState(0);
  const monacoRef = useRef(null);
  const themes = useTheme();
  const [code, setCode] = useState();
  const [productLogo, setProductLogo] = useState('');
  const [browserTabLogo, setBrowserTabLogo] = useState('');
  const [clientLogo, setClientLogo] = useState('');
  const [productName, setProductName] = useState('');
  const [showIconDialog, setShowIconDialog] = useState(false);
  const [iconDetail, setIconDetail] = useState('');

  const handleEditorDidMount = useCallback((monaco) => {
    monacoRef.current = monaco;
    utils.applyThemeToMonacoEditor(monaco, themes, userTheme);
  }, []);

  useEffect(() => {
    if (monacoRef.current) {
      utils.applyThemeToMonacoEditor(monacoRef.current, themes, userTheme);
    }
  }, [userTheme]);
  useEffect(() => {
    if (activatedOrgName !== orgName) {
      navigate('/application-setting');
    }
    fetchThemeConfiguration(orgName, clientName, LIGHT_THEME_NAME).then(
      (res) => {
        if (res?.data) {
          setProductName(res.data?.product_name);
          setBrowserTabLogo(res.data?.browser_icon);
          setProductLogo(res.data?.product_icon);
          setClientLogo(res.data?.client_icon);
          setCode(JSON.stringify(res.data?.saved_theme, null, 2));
        }
      }
    );
  }, [orgName, activatedOrgName]);

  function isValidColor(color) {
    const condition =
      REGEX.HEX_REGEX.test(color) ||
      REGEX.RGB_REGEX.test(color) ||
      REGEX.RGBA_REGEX.test(color) ||
      REGEX.HSL_REGEX.test(color);
    return (
      condition || REGEX.HSLA_REGEX.test(color) || REGEX.COLOR_REGEX.test(color)
    );
  }
  const validatePaletteColors = (palette) => {
    return palette
      ? Object.values(palette).every((color) => isValidColor(color))
      : false;
  };

  const saveCodeLocally = useCallback(() => {
    try {
      const parsedCode = JSON.parse(code);
      const isValidPalettePrimary = validatePaletteColors(
        parsedCode?.palette?.primary
      );
      const isValidPaletteSecondary = validatePaletteColors(
        parsedCode?.palette?.secondary
      );
      if (isValidPalettePrimary && isValidPaletteSecondary) {
        const body = {
          product: clientName,
          saved_theme: parsedCode,
          theme: tabIndex === 0 ? LIGHT_THEME_NAME : DARK_THEME_NAME,
        };
        saveTheme(orgName, body).then(() => {
          SwalToast({
            icon: 'success',
            title: 'Code has been saved successfully.',
          });
        });
      } else {
        SwalToast({
          icon: 'error',
          title: 'Provided color palette is not supported.',
        });
      }
    } catch (error) {
      SwalToast({
        icon: 'error',
        title: 'Provided code is not valid JSON!',
      });
    }
  }, [code, validatePaletteColors, saveTheme, tabIndex, user]);

  const onCancelClick = useCallback(() => {
    fetchThemeConfiguration(
      orgName,
      clientName,
      tabIndex === 0 ? LIGHT_THEME_NAME : DARK_THEME_NAME,
      false
    ).then((res) => {
      if (res?.data) {
        setProductName(res.data?.product_name);
        setBrowserTabLogo(res.data?.browser_icon);
        setProductLogo(res.data?.product_icon);
        setClientLogo(res.data?.client_icon);
        setCode(JSON.stringify(res.data?.saved_theme, null, 2));
      }
    });
  }, [
    setProductName,
    setBrowserTabLogo,
    setProductLogo,
    setClientLogo,
    setCode,
    fetchThemeConfiguration,
    tabIndex,
  ]);

  const onResetClick = (iconType) => {
    const currentThemeMode =
      tabIndex === 0 ? LIGHT_THEME_NAME : DARK_THEME_NAME;
    const body = {
      product: clientName,
      theme: currentThemeMode,
    };
    switch (iconType) {
      case PRODUCT_ICON_LOGO:
        body.icon = 'product_icon';
        resetToDefaultTheme(orgName, body).then((res) => {
          setProductLogo(res.data?.product_icon);
        });
        break;
      case BROWSER_ICON_LOGO:
        body.icon = 'browser_icon';
        resetToDefaultTheme(orgName, body).then((res) => {
          setBrowserTabLogo(res.data?.browser_icon);
          setProductName(res.data?.product_name);
        });
        break;
      case CLIENT_ICON_LOGO:
        body.icon = 'client_icon';
        resetToDefaultTheme(orgName, body).then((res) => {
          setClientLogo(res.data?.client_icon);
        });
        break;
      default:
        break;
    }
  };

  const onResetToDefaultClick = useCallback(() => {
    const currentThemeMode =
      tabIndex === 0 ? LIGHT_THEME_NAME : DARK_THEME_NAME;
    const body = {
      product: clientName,
      theme: currentThemeMode,
    };
    resetToDefaultTheme(orgName, body).then((res) => {
      setCode(JSON.stringify(res?.data?.saved_theme, null, 2));
    });
    SwalToast({
      icon: 'success',
      title: 'Code has been reset to default.',
    });
  }, [saveTheme]);
  const handleTabChange = useCallback(
    (event, newValue) => {
      setTabIndex(newValue);
      if (newValue === 0 || newValue === 1) {
        fetchThemeConfiguration(
          orgName,
          clientName,
          newValue === 0 ? LIGHT_THEME_NAME : DARK_THEME_NAME
        ).then((res) => {
          if (res?.data) {
            setProductName(res.data?.product_name);
            setBrowserTabLogo(res.data?.browser_icon);
            setProductLogo(res.data?.product_icon);
            setClientLogo(res.data?.client_icon);
            setCode(JSON.stringify(res.data?.saved_theme, null, 2));
          }
        });
      }
    },
    [
      setTabIndex,
      fetchThemeConfiguration,
      setProductName,
      setBrowserTabLogo,
      setCode,
    ]
  );

  const handleGoBack = () => {
    window.history.back();
  };

  const IconCard = ({ iconType, logo }) => {
    const base64 = logo;
    return (
      <Box
        sx={{
          border: `1px solid ${themes.palette.loginPage.loginFormBorder}`,
          borderRadius: '8px',
          padding: '16px',
          minWidth: '350px',
        }}
      >
        {/* Title and Subtitle */}
        <Typography variant="subtitle2">{iconType}</Typography>
        <Typography variant="body1" mb={2} color={themes.palette.other.gray6}>
          {`Update your ${iconType.toLowerCase()}.`}
        </Typography>

        {/* Image Container with dashed border */}
        <Box
          sx={{
            border: `2px dashed ${themes.palette.loginPage.loginFormBorder}`,
            borderRadius: '8px',
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            mt: 1,
            p: 2,
          }}
        >
          <img
            src={base64}
            alt={iconType}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          />
        </Box>

        {/* Button */}

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => {
              onResetClick(iconType);
            }}
            title="Reset"
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowIconDialog(true);
              setIconDetail(iconType);
            }}
          >
            Change Icon
          </Button>
        </Box>
      </Box>
    );
  };

  IconCard.propTypes = {
    iconType: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
  };

  return (
    <Box display="flex" flexDirection="column" margin="20px">
      <Box>
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            component="ol"
            sx={{
              listStyleType: 'none',
              padding: '0px',
              margin: '0px',
            }}
          >
            <IconButton
              onClick={handleGoBack}
              title="Go back"
              aria-label="Back"
              color="primary"
            >
              <ArrowBackIcon aria-label="Go Back" />
            </IconButton>
            <Typography variant="subtitle2" component="li">
              Application
            </Typography>

            <ChevronRightIcon />
            <Typography variant="subtitle2" component="li">
              {productDetails?.name}
            </Typography>

            <ChevronRightIcon />
            <Typography variant="subtitle1" component="li">
              Theme configuration
            </Typography>
          </Box>
        </Grid>
      </Box>
      <Grid container mt={2}>
        <Grid item xs={12}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Light theme" title="Light theme" />
            <Tab label="Dark theme" title="Dark theme" />
          </Tabs>
        </Grid>
      </Grid>
      <Grid container mt={3}>
        {(tabIndex === 0 || tabIndex === 1) && (
          <>
            <Grid container direction="row" gap={2} mb={1}>
              <IconCard iconType={PRODUCT_ICON_LOGO} logo={productLogo} />
              <IconCard iconType={BROWSER_ICON_LOGO} logo={browserTabLogo} />
              <IconCard iconType={CLIENT_ICON_LOGO} logo={clientLogo} />
            </Grid>
            <Grid item pr={2} mt={1} xs={12} pl="12px" mr={2}>
              <Typography
                variant="body4"
                tabIndex={0}
                role="note"
                aria-label="Important note: This page allows you to customize the visual elements of the
                  product including the favicon, product icon, and theme. For
                  more information, see the default theme viewer. The ideal
                  image size for the product icon is 88px x 40px with an aspect
                  ratio of 2:1."
                sx={{
                  fontStyle: 'normal',
                  border: 'none',
                  '&:focus': {
                    border: '2px solid',
                    borderColor: 'primary.main',
                  },
                }}
              >
                <strong>Note:</strong>&nbsp;
                <i>
                  This page allows you to customize the visual elements of the
                  product including the favicon, product icon, and theme. For
                  more information, see the default theme viewer. The ideal
                  image size for the product icon is 88px x 40px with an aspect
                  ratio of 2:1.
                </i>
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                '& .lines-content.monaco-editor-background': {
                  paddingLeft: '24px !important',
                },
              }}
              mt={1}
              mb={8}
            >
              <Editor
                height="65vh"
                defaultLanguage="JSON"
                value={code}
                theme={
                  themes.palette.mode === 'dark'
                    ? 'myThemeDark'
                    : 'myThemeLight'
                }
                beforeMount={handleEditorDidMount}
                options={{
                  fontSize: 16,
                  lineHeight: 22,
                  fontFamily: 'Open Sans',
                  lineDecorationsWidth: 5,
                  padding: {
                    top: 12,
                    bottom: 12,
                    left: 12,
                    right: 12,
                  },
                  scrollbar: {
                    vertical: 'auto',
                    horizontal: 'auto',
                    verticalScrollbarSize: 5,
                    horizontalScrollbarSize: 5,
                  },
                }}
                onChange={(value) => setCode(value)}
                editorDidMount={(editor) => {
                  setTimeout(() => {
                    editor.getAction('editor.action.formatDocument').run();
                  }, 300);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                height="60px"
                backgroundColor={themes.palette.background.paper}
                display="flex"
                alignItems="center"
                justifyContent="flex-end"
                position="fixed"
                zIndex={100}
                bottom={0}
                left="260px"
                width="calc(100% - 260px)"
              >
                <Box
                  pr="12px"
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  mr={2}
                >
                  <Button
                    color="primary"
                    variant="outlined"
                    size="large"
                    onClick={onResetToDefaultClick}
                    title="Reset to default"
                    fullWidth="true"
                  >
                    Reset to default
                  </Button>
                  <Button
                    color="primary"
                    variant="outlined"
                    size="large"
                    onClick={onCancelClick}
                    title="Cancel"
                    fullWidth="true"
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    size="large"
                    onClick={saveCodeLocally}
                    title="Save"
                    fullWidth="true"
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Grid>
          </>
        )}
        {showIconDialog && (
          <AddIconDialog
            open={showIconDialog}
            handleClose={() => setShowIconDialog(false)}
            iconType={iconDetail}
            productName={productName}
            setProductName={setProductName}
            clientName={clientName}
            orgName={orgName}
            tabIndex={tabIndex}
            fetchThemeConfiguration={fetchThemeConfiguration}
            setBrowserTabLogo={setBrowserTabLogo}
            setProductLogo={setProductLogo}
            setClientLogo={setClientLogo}
          />
        )}
      </Grid>
    </Box>
  );
};

export default withTransaction(
  'CustomThemeConfig',
  'component'
)(CustomThemeConfig);
